import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { ShieldCheck, MapPin, Eye, Lock } from 'lucide-react';
import { strings } from '@/data/strings';
import { Button } from '@/components/Button';
import styles from './Hero.module.css';

const DOWNLOAD_HREF = '#descargar';
const PROFESSIONALS_HREF = '#para-profesionales';
const SPLASH_MS = 2000;

// Order must match strings.hero.values: Confianza, Cercanía, Visibilidad, Privacidad
const VALUE_ICONS = [ShieldCheck, MapPin, Eye, Lock];

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

export function Hero() {
  const [mounted, setMounted] = useState(false);
  const [splashGone, setSplashGone] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => setSplashGone(true), SPLASH_MS);
    return () => window.clearTimeout(t);
  }, []);

  const { values } = strings.hero;

  return (
    <section
      id="top"
      className={cx(styles.section, mounted && styles.mounted)}
    >
      <div className={styles.bgGlow} aria-hidden="true" />

      <div className={cx('container', styles.inner)}>
        <div className={styles.copy}>
          <h1 className={styles.title} style={{ '--i': 0 } as CSSProperties}>
            {strings.hero.title}
          </h1>

          <p className={styles.subtitle} style={{ '--i': 1 } as CSSProperties}>
            {strings.hero.subtitle}
          </p>

          <div className={styles.ctas} style={{ '--i': 2 } as CSSProperties}>
            <Button as="a" href={DOWNLOAD_HREF} variant="primary" size="lg">
              {strings.hero.ctaPrimary}
            </Button>
            <a href={PROFESSIONALS_HREF} className={styles.secondary}>
              <span>{strings.hero.ctaSecondary}</span>
              <span className={styles.secondaryArrow} aria-hidden="true">→</span>
            </a>
          </div>
        </div>

        <div className={styles.visual} aria-hidden="true">
          <div className={styles.phone}>
            <div className={styles.phoneShell}>
              <div className={styles.screen}>
                <div className={cx(styles.splash, splashGone && styles.splashGone)} aria-hidden="true">
                  <span className={styles.splashWordmark}>
                    <span>Profind</span>
                    <span className={styles.splashDot} />
                  </span>
                </div>
                <div className={cx(styles.phoneValues, splashGone && styles.phoneValuesReveal)}>
                  {values.map((v, i) => {
                    const Icon = VALUE_ICONS[i];
                    return (
                      <div
                        key={v.label}
                        className={cx(styles.phoneValueItem, styles[`tone-${v.tone}`])}
                        style={{ '--vi': i } as CSSProperties}
                      >
                        <Icon
                          className={styles.phoneValueIcon}
                          size={30}
                          strokeWidth={1.6}
                          aria-hidden="true"
                        />
                        <span className={styles.phoneValueLabel}>{v.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <span className={styles.srOnly}>{strings.hero.visualAlt}</span>
      </div>
    </section>
  );
}
